/**
 * 2차 AI 입시분석 워크스페이스 GNB·랜딩 축 선언 배열 SSoT.
 *
 * WorkspaceShell(헤더 메가메뉴)과 랜딩(`landingAxes.ts` 가 이 배열을 파생해 축 카드로 그림)이 이 단일
 * 배열을 공유한다(항목 추가 = 배열 1행, 하드코딩 금지 — AppHeader 의 config/nav.ts HEADER_GROUPS 패턴과
 * 동일 철학). 034 가 "2차 오픈 시 후속"으로 미뤘던 landingAxes↔workspaceNav 이중 선언은 075 에서 여기로
 * 통합됐다 — 축명·설명·아이콘·대표 route 를 다른 파일에 다시 쓰지 말 것.
 *
 * ⚠️ 도메인 데이터라 config/(도메인 무지 인프라)가 아닌 data/analysis/ 에 둔다
 *    (docs/frontend-domain-structure-proposal.md §71 — components/layout·ui·lib·config 성격은 도메인 무지).
 *
 * 대분류 5축: 내 입시카드 / 성적 분석 / 진로심리검사 / AI 종합분석 / 리포트 보관함.
 * "AI 분석"(대학 추천·세특 추천·AI 상담)은 3단계 별도 합의 확정 후 추가 — 현재 화면 미노출
 * (하단 보류 주석 블록 참조).
 *
 * 활성(status:'active')은 내 입시카드 3항목·정시/수시 분석·진로심리검사 4종·AI 종합분석·리포트 보관함.
 * 나머지는 status:'soon'(준비중, 클릭 비활성)으로 앞으로 붙어 나갈 로드맵을 드러낸다.
 */

import {
  ARCHIVE_KINDS,
  REPORT_REGISTRY,
  isArchiveKind,
  reportArchiveRoute,
} from './reportRegistry';

/** 하위 기능 항목. active 만 route 를 가지며, soon 은 "준비중" 비활성 표시. */
export interface WorkspaceSubItem {
  /** 하위 기능명(서술형). */
  label: string;
  /** 활성 항목의 라우트 경로. soon 항목은 미보유(클릭 비활성). */
  route?: string;
  /** active=이동 가능 / soon=준비중(비활성). */
  status: 'active' | 'soon';
  /** 드롭다운·카드에 노출할 보조 설명(선택). */
  desc?: string;
  /**
   * 통합 검색(078) 동의어 — 라벨에 없는 말만 적는다(라벨은 이미 검색된다). 검색 기능 사전은 이 배열에서
   * 파생되므로(`searchFunctionIndex.ts`) 검색어를 다른 파일에 따로 두지 않는다.
   */
  keywords?: string[];
  /** 통합 검색(078) 히어로 추천 칩에 노출 — 칩은 이 플래그에서 파생된다(라벨 문자열 재기입 금지). */
  searchSuggested?: true;
}

/** 대분류 식별자 — 라벨은 바뀔 수 있어 코드 참조는 key 로 한다(랜딩 축 key 와 동일). */
export type WorkspaceNavKey = 'card' | 'score' | 'test' | 'ai' | 'reports';

/** 랜딩 축 카드 전용 표시값 — GNB 는 읽지 않는다. */
export interface WorkspaceLandingCard {
  /** 카드 우하단 태그(리드 카드는 배지). */
  tag: string;
  /** 리드 카드(전폭 배치) — 선언상 1개. */
  lead?: true;
  /** 하위 기능 칩(체크 아이콘) — 축 안에 무엇이 있는지 드러낸다. */
  sub?: string[];
  /**
   * 서비스 카드 2줄 소개(Figma 랜딩 2026-09) — `\n` 으로 줄을 가른다. GNB summary 보다 짧은 카드 전용
   * 카피라 별도 보유(랜딩 파일에 재기입하지 않는다 — 여기가 SSoT).
   */
  blurb: string;
  /** 서비스 카드 우측 일러스트 키 — `public/assets/landing/art-{art}.webp`. */
  art: WorkspaceLandingArt;
}

/** 서비스 카드 일러스트 키(Figma 3D 아트워크 — public/assets/landing/art-*.webp). */
export type WorkspaceLandingArt = 'card' | 'score' | 'test' | 'ai' | 'reports' | 'interview';

/** GNB 대분류(컨설팅 목적 축). */
export interface WorkspaceNavGroup {
  key: WorkspaceNavKey;
  /** 대분류명(서술형). */
  label: string;
  /** 진입 홈 카드 헤더 아이콘(lucide-vue-next 아이콘 컴포넌트명 kebab → 매핑은 페이지에서). */
  icon: WorkspaceNavIcon;
  /** 이 대분류를 대표하는 한 줄 설명(진입 홈 카드용). */
  summary: string;
  /** 대표 진입 route — 랜딩 축 카드 클릭 목적지(하위 항목 route 와 별개). */
  route: string;
  /** 랜딩 축 카드 표시값. */
  landing: WorkspaceLandingCard;
  /** 하위 기능 목록. */
  items: WorkspaceSubItem[];
}

/** 대분류 아이콘 키(홈 카드에서 lucide 컴포넌트로 매핑). */
export type WorkspaceNavIcon = 'clipboard' | 'target' | 'list-checks' | 'archive' | 'monitor';

/** 내 입시카드(WS-01) — 성적 입력 허브. 분석 진입 전 성적이 없으면 여기로 보낸다. */
export const IPSI_CARD_ROUTE = '/analysis/card';
/** 성적 분석(정시) — 저장된 수능 성적을 소비하는 분석 화면. */
export const QUANT_JEONGSI_ROUTE = '/analysis/jeongsi';
/** 수시 6장 전략 생성 화면(060). */
export const SUSI_CARDS_ROUTE = '/analysis/susi/cards';

export const WORKSPACE_NAV: WorkspaceNavGroup[] = [
  {
    // 모든 분석이 공유하는 내 성적·희망 정보. 한 번 저장하면 재입력 불필요.
    key: 'card',
    label: '내 입시카드',
    icon: 'clipboard',
    summary: '이름·학년·희망 계열과 성적을 한 번 저장하면 모든 분석이 가져다 씁니다.',
    route: IPSI_CARD_ROUTE,
    landing: {
      tag: '내 성적·희망 정보',
      lead: true,
      sub: ['수능·내신 성적 저장', '희망 계열·관심 대학', '한 번 입력, 계속 사용'],
      blurb: '한 눈에 보는\n나의 입시 정보',
      art: 'card',
    },
    // WS-01(035) 오픈 — 3섹션이 `/analysis/card` **단일 화면**이라 하위 항목은 같은 화면의
    // 앵커로 연결한다(IA D-3 확정). 앵커 id 는 CardSection 의 `anchor` prop 이 소유.
    items: [
      {
        label: '기본 정보',
        route: `${IPSI_CARD_ROUTE}#basic`,
        status: 'active',
        desc: '학년·희망 계열·고교 정보.',
      },
      {
        label: '수능 성적 입력',
        keywords: ['수능', '모의고사'],
        searchSuggested: true,
        route: `${IPSI_CARD_ROUTE}#suneung`,
        status: 'active',
        desc: '국·수·탐 백분위(정시 분석 입력).',
      },
      // ⚖️ 내신은 학생 수동 입력만 가능 — 학생부 HTML·PDF 업로드/OCR 자동입력은 법령상 불가.
      {
        label: '내신 성적 입력',
        keywords: ['내신', '등급'],
        searchSuggested: true,
        route: `${IPSI_CARD_ROUTE}#naesin`,
        status: 'active',
        desc: '교과 등급 직접 입력(수시 분석 입력).',
      },
      // ⚖️ 폐기 — 비교과(서술형) 분석·평가는 「초·중등교육법」 제25조의2 위반.
      //    근거: claudedocs/legal/02-학생부-비교과-이용제한.md (오픈 검토 전 반드시 읽을 것).
    ],
  },
  {
    key: 'score',
    label: '성적 분석',
    icon: 'target',
    summary: '수능·내신 성적으로 내 점수에 맞는 지원 범위를 살펴봅니다.',
    route: QUANT_JEONGSI_ROUTE,
    // 정시 학과 AI 해석(057)·수시 6장 전략(060)은 이 축 안에 있다 — 랜딩 칩으로 드러낸다(075).
    landing: {
      tag: '정시·수시',
      sub: ['정시 분석 · 예상 합격선', '학과별 AI 해석', '수시 6장 전략'],
      blurb: '내 성적으로\n지원 가능한 대학은?',
      art: 'score',
    },
    items: [
      {
        label: '정시 분석',
        keywords: ['정시', '진단'],
        route: QUANT_JEONGSI_ROUTE,
        status: 'active',
        desc: '수능 성적과 예상 합격선을 비교합니다.',
      },
      {
        label: '수시 분석',
        keywords: ['수시', '6장', '카드'],
        route: '/analysis/susi',
        status: 'active',
        desc: '대학×전형별 합격자 등급·조건·준비 방향.',
      },
      // "진단 결과" 항목 제거(IA D-5) — 진단은 분석 화면에서 생성→상세 이동, 이력은 내 리포트 귀속.
    ],
  },
  {
    key: 'test',
    label: '진로심리검사',
    icon: 'list-checks',
    summary: '진로·학습·심리·진학 준비도 4종 검사를 받고 결과를 리포트로 봅니다.',
    route: '/analysis/tests',
    landing: { tag: '검사 4종', blurb: '나에게 맞는\n진로와 학과 찾기', art: 'test' },
    items: [
      {
        label: '진로 검사',
        route: '/analysis/tests',
        status: 'active',
        desc: '홀랜드 이론 기반 진로 흥미 진단.',
      },
      {
        label: '학습 전략 검사',
        route: '/analysis/tests',
        status: 'active',
        desc: '학습 전략·습관 진단.',
      },
      {
        label: '심리 검사',
        route: '/analysis/tests',
        status: 'active',
        desc: '심리·정서 상태 진단.',
      },
      {
        label: '진학 준비도 검사',
        route: '/analysis/tests',
        status: 'active',
        desc: '진학 준비 상태 진단.',
      },
    ],
  },
  {
    // ⑤ AI 종합분석(045 WS-08·09) — 여정 순서상 검사 다음(IA D-1). 하위 2항목은 WS-08
    // 단일 화면의 기능 소개 역할이며 앵커 없이 페이지 상단으로 진입한다(두 구획이 한
    // 뷰포트에 함께 보여 스크롤 이동의 실효가 없음 — ③ 진로심리검사 동일 관행).
    key: 'ai',
    label: 'AI 종합분석',
    icon: 'monitor',
    summary: '성적·검사·지원 후보를 함께 읽어 AI 종합분석 결과를 만듭니다.',
    route: '/analysis/ai',
    landing: { tag: 'AI 종합분석 결과', blurb: '데이터로 찾는\n최적의 합격 전략', art: 'ai' },
    items: [
      {
        label: '지원 후보',
        route: '/analysis/ai',
        status: 'active',
        desc: '정시·수시에서 담은 대학·학과를 관리합니다.',
      },
      {
        label: 'AI 종합분석 결과',
        keywords: ['ai', '종합', '소견'],
        route: '/analysis/ai',
        status: 'active',
        desc: '성적·검사·지원 후보를 함께 분석한 결과를 만듭니다.',
      },
    ],
  },
  {
    // 리포트 보관함(065 WS-13) — 불변 리포트의 유일한 이력 허브. 하위 항목은 레지스트리 순 표시 종류 4종
    // 필터 딥링크(`?kind=`)이며 라벨·설명도 레지스트리 SSoT 에서 읽는다(재기입 금지). 정시 학과 AI 해석은 "성적 분석 결과"에 접힌다(072).
    key: 'reports',
    label: '리포트 보관함',
    icon: 'archive',
    summary: '검사·성적 분석·AI 종합분석에서 만든 결과를 한곳에서 다시 봅니다.',
    route: reportArchiveRoute(),
    landing: { tag: '결과 이력', blurb: '나의 분석 결과를\n언제든 다시 확인', art: 'reports' },
    items: ARCHIVE_KINDS.map((kind) => ({
      label: REPORT_REGISTRY[kind].label,
      keywords: ['보관함', '리포트', '이력'],
      route: reportArchiveRoute(kind),
      status: 'active' as const,
      desc: REPORT_REGISTRY[kind].desc,
    })),
  },
  // ⏸ 보류 — 대학 추천·세특 추천·AI 상담은 3단계 별도 합의 확정 후 ⑤ 그룹 하위로 확장(현재 미노출).
];

/** breadcrumb 한 마디(대분류/현재 화면). route 있으면 링크, 없으면 텍스트. */
export interface WorkspaceCrumb {
  label: string;
  route?: string;
}

interface WorkspacePageLocation {
  groupLabel: string;
  /** 대분류 crumb 의 복귀 링크(선택) — 보관함 귀속 상세는 해당 종류 필터로 돌아간다(065). */
  groupRoute?: string;
  pageLabel: string;
  /** 대분류와 현재 화면 사이의 중간 조각(062 US3) — route 를 채우면 링크로 렌더된다. */
  middle?: WorkspaceCrumb;
  /** 경로·해시·쿼리로 현재 화면을 판정한다 — `?kind=` 필터 화면은 query 로 라벨을 가른다(065). */
  matches: (path: string, hash: string, query: URLSearchParams) => boolean;
}

/** 보관함 그룹 라벨 — 원천 5종 상세·표시 종류 4 필터 화면이 전부 이 그룹에 귀속된다(065 FR-020). */
const ARCHIVE_GROUP = '리포트 보관함';

/** 보관함 필터 화면의 pageLabel — `?kind=` 가 표시 종류(4)면 그 라벨, 아니면 그룹 라벨(전체). */
function archivePageLabel(query: URLSearchParams): string {
  const kind = query.get('kind');
  return isArchiveKind(kind) ? REPORT_REGISTRY[kind].label : ARCHIVE_GROUP;
}

/** 메뉴 항목과 독립적인 실제 페이지·탭 위치 라벨 SSoT. 구체 경로를 접두 경로보다 먼저 둔다. */
const WORKSPACE_PAGE_LOCATIONS: WorkspacePageLocation[] = [
  // 6장 카드 조정(063) — 편집은 수시 분석 흐름(재열람 접두 매처보다 먼저, 구체 경로 우선).
  {
    groupLabel: '성적 분석',
    pageLabel: '6장 카드 조정',
    middle: { label: '수시 분석', route: '/analysis/susi' },
    matches: (path) => /^\/analysis\/susi\/cards\/\d+\/edit$/.test(path),
  },
  // 수시 6장 리포트 재열람(060·065) — 불변 리포트 상세라 보관함 귀속. 대분류 crumb 이 필터 복귀 링크.
  {
    groupLabel: ARCHIVE_GROUP,
    groupRoute: reportArchiveRoute('SUSI_CARDS'),
    pageLabel: REPORT_REGISTRY.SUSI_CARDS.label,
    matches: (path) => /^\/analysis\/susi\/cards\/\d+$/.test(path),
  },
  // 수시 6장 생성 화면 — 정확 일치만(재열람·편집은 위 매처가 먼저 잡는다).
  {
    groupLabel: '성적 분석',
    pageLabel: '수시 6장 전략',
    middle: { label: '수시 분석', route: '/analysis/susi' },
    matches: (path) => path === SUSI_CARDS_ROUTE,
  },
  // 정시 학과 AI 해석 재열람(065 WS-14) — 보관함 복귀는 "성적 분석 결과" 필터(072, 표시 종류 접힘). 페이지 라벨은 상세 페이지 소관.
  {
    groupLabel: ARCHIVE_GROUP,
    groupRoute: reportArchiveRoute('DIAGNOSIS'),
    pageLabel: REPORT_REGISTRY.JEONGSI_DEPT_OPINION.label,
    matches: (path) => path.startsWith('/analysis/jeongsi/opinions/'),
  },
  {
    groupLabel: ARCHIVE_GROUP,
    groupRoute: reportArchiveRoute('OPINION'),
    pageLabel: REPORT_REGISTRY.OPINION.label,
    matches: (path) => path.startsWith('/analysis/ai/opinions/'),
  },
  {
    groupLabel: ARCHIVE_GROUP,
    groupRoute: reportArchiveRoute('DIAGNOSIS'),
    pageLabel: REPORT_REGISTRY.DIAGNOSIS.label,
    matches: (path) => path.startsWith('/analysis/diagnosis/'),
  },
  {
    groupLabel: ARCHIVE_GROUP,
    groupRoute: reportArchiveRoute('ASSESSMENT'),
    pageLabel: REPORT_REGISTRY.ASSESSMENT.label,
    matches: (path) => path.startsWith('/analysis/results/'),
  },
  // 보관함 필터 화면 — pageLabel 은 `?kind=` 로 결정(전체면 그룹 라벨 = 한 마디만).
  {
    groupLabel: ARCHIVE_GROUP,
    groupRoute: '/analysis/reports',
    pageLabel: ARCHIVE_GROUP,
    matches: (path) => path === '/analysis/reports',
  },
  {
    groupLabel: '내 입시카드',
    pageLabel: '내 입시카드',
    matches: (path) => path === IPSI_CARD_ROUTE,
  },
  {
    groupLabel: '성적 분석',
    pageLabel: '정시 분석',
    matches: (path) => path === QUANT_JEONGSI_ROUTE,
  },
  {
    groupLabel: '성적 분석',
    pageLabel: '수시 분석',
    matches: (path) => path === '/analysis/susi',
  },
  {
    groupLabel: '진로심리검사',
    pageLabel: '진로심리검사',
    matches: (path) => path === '/analysis/tests' || path.startsWith('/analysis/tests/'),
  },
  {
    groupLabel: 'AI 종합분석',
    pageLabel: 'AI 종합분석',
    matches: (path) => path === '/analysis/ai',
  },
];

/**
 * 하위 페이지 route 로 소속 대분류·현재 화면을 역추적해 breadcrumb 조각을 만든다(SSoT 재사용).
 * WorkspaceBreadcrumb 이 '홈'을 앞에 붙이므로 여기선 [대분류, (중간), 현재화면]만 반환.
 *
 * 1차: 정확 일치(기존 규칙, GNB 등록 route 그대로 진입한 화면).
 * 동적 상세·hash 탭을 구체적인 순서로 판정하며, 매칭 실패 시 빈 배열을 반환한다.
 * 중간 조각(middle)에 route 가 있으면 WorkspaceBreadcrumb 이 RouterLink 로 렌더한다(062 US3).
 */
export function crumbsForRoute(fullPath: string): WorkspaceCrumb[] {
  const url = new URL(fullPath, 'https://workspace.local');
  const location = WORKSPACE_PAGE_LOCATIONS.find((candidate) =>
    candidate.matches(url.pathname, url.hash, url.searchParams),
  );
  if (!location) return [];
  // 보관함 필터 화면은 kind 쿼리가 페이지 라벨을 가른다(065) — 전체면 그룹 한 마디만.
  const pageLabel =
    url.pathname === '/analysis/reports' ? archivePageLabel(url.searchParams) : location.pageLabel;
  if (location.groupLabel === pageLabel) return [{ label: pageLabel }];
  const crumbs: WorkspaceCrumb[] = [{ label: location.groupLabel, route: location.groupRoute }];
  if (location.middle) crumbs.push(location.middle);
  crumbs.push({ label: pageLabel });
  return crumbs;
}
